import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react";
import { ArrowRight, Check, Copy, Eye, EyeOff, KeyRound, Lock, Mail, ShieldCheck, UserRound, X } from "lucide-react";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { InlineAlert } from "./feedback";

/**
 * The staff sign-in of the NCP apps (back office, Platform Console), security §14.1–§14.2 with SPEC C13/C14: password →
 * (forced password change → authenticator enrolment) → authenticator code. Presentational: the flow comes from
 * `usePasswordSignIn` (`@ncp/api-client`) and the words from the app's `auth.*` strings, so this package keeps no i18n or API
 * dependency. ECONIC draws its own card (apps/assistant/src/econic/login/signin) on the same flow and the same `OtpInput`.
 *
 * Stable selectors (puppeteer and the accessibility audit): `input[name=login|password|otp|newPassword|confirmPassword]`,
 * `[data-testid=signin-submit|mfa-submit|password-change-submit|enrol-submit|password-toggle|password-rules|totp-qr|totp-secret]`,
 * `[data-testid=signin-error][data-code=…]`.
 */

// ---------------------------------------------------------------- one-time code: one real input drawn as 6 cells (3 + dash + 3)

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once 6 digits are in (typed, pasted or autofilled). */
  onComplete?: (value: string) => void;
  id?: string;
  name?: string;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  "aria-label"?: string;
  "aria-describedby"?: string;
  className?: string;
  /** Classes of each drawn cell, of the cell that takes the next digit, and of the dash between the two groups. */
  cellClassName?: string;
  activeCellClassName?: string;
  dashClassName?: string;
}

/**
 * `input[name="otp"][autocomplete="one-time-code"][inputmode="numeric"][maxlength=6]`, `dir="ltr"` in both languages. The input sits
 * over the cells, so typing, pasting and the phone's code autofill all go to one field; the cells only draw what it holds.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  id,
  name = "otp",
  disabled,
  invalid,
  autoFocus,
  className,
  cellClassName,
  activeCellClassName,
  dashClassName,
  ...aria
}: OtpInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const digits = value.replace(/\D/g, "").slice(0, 6);
  const completed = useRef<string | null>(null);

  useEffect(() => {
    if (digits.length === 6 && completed.current !== digits) {
      completed.current = digits;
      onComplete?.(digits);
    }
    if (digits.length < 6) completed.current = null;
  }, [digits, onComplete]);

  const set = (raw: string) => onChange(raw.replace(/\D/g, "").slice(0, 6));
  const active = Math.min(digits.length, 5);
  const cell = (i: number) => (
    <span
      key={i}
      aria-hidden
      className={cn(
        "flex h-12 min-w-0 flex-1 items-center justify-center rounded-[10px] border bg-white text-[21px] font-semibold tabular-nums text-aeblack-950 transition-[border-color,box-shadow]",
        invalid ? "border-danger-500" : "border-sand-300",
        cellClassName,
        focused && i === active && !disabled && cn("border-aegold-500 ring-2 ring-aegold-500/40", activeCellClassName),
      )}
      data-active={focused && i === active ? "" : undefined}
    >
      {digits[i] ?? (focused && i === active ? <span className="otp-caret h-[22px] w-[1.5px] animate-[otp-blink_1.1s_steps(1)_infinite] bg-current motion-reduce:animate-none" /> : null)}
    </span>
  );

  return (
    <div dir="ltr" className={cn("relative flex items-center gap-2", className)} onClick={() => ref.current?.focus()}>
      <style>{"@keyframes otp-blink{50%{opacity:0}}"}</style>
      {[0, 1, 2].map(cell)}
      <span aria-hidden className={cn("h-0.5 w-2 flex-none rounded-full bg-sand-300", dashClassName)} />
      {[3, 4, 5].map(cell)}
      <input
        ref={ref}
        id={id}
        name={name}
        type="text"
        dir="ltr"
        value={digits}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="one-time-code"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        spellCheck={false}
        aria-invalid={invalid || undefined}
        {...aria}
        onFocus={(e) => {
          setFocused(true);
          // A full field is selected on focus, so typing a new code replaces it (maxlength would otherwise swallow the digits).
          if (e.currentTarget.value.length >= 6) e.currentTarget.select();
        }}
        onBlur={() => setFocused(false)}
        onChange={(e) => set(e.target.value)}
        onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
          e.preventDefault();
          set(e.clipboardData.getData("text"));
        }}
        className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent shadow-none outline-none selection:bg-transparent focus:shadow-none focus-visible:shadow-none"
        style={{ boxShadow: "none" }}
      />
    </div>
  );
}

/** Caps Lock state from the last key event in a field (null until a key was pressed there). */
export function useCapsLock(): [boolean | null, (e: KeyboardEvent<HTMLInputElement>) => void] {
  const [on, setOn] = useState<boolean | null>(null);
  return [on, (e) => setOn(typeof e.getModifierState === "function" ? e.getModifierState("CapsLock") : null)];
}

// ---------------------------------------------------------------- the card

export interface StaffSignInFlow {
  step: "password" | "mfa" | "password_change" | "mfa_enrol" | "done";
  busy: boolean;
  error: { code: string; attemptsLeft?: number | null; errors?: string[] } | null;
  login: string;
  firstLogin: boolean;
  enrolment: { qrSvgDataUri: string; secret: string; account: string; issuer: string } | null;
  submitPassword: (login: string, password: string) => Promise<void>;
  changePassword: (newPassword: string, confirm?: string) => Promise<void>;
  confirmEnrolment: (code: string) => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  restart: () => void;
  fail: (code: string) => void;
  clearError: () => void;
}

export interface StaffSignInCardProps {
  flow: StaffSignInFlow;
  /** `auth.*` strings: `t("code.verify")` → the app's `auth.code.verify`. */
  t: (key: string, vars?: Record<string, unknown>) => string;
  /** "n•••@moet.gov.ae" for the code step. */
  maskedLogin: string;
  /** Advisory checks of the new password (the server's policy decides). */
  rules: (password: string, confirm: string) => { length: boolean; notTooLong: boolean; noIdentity: boolean; matches: boolean };
  /** A notice above the form: why the person is here (`/login?reason=idle`), a Ministry-account failure. */
  notice?: ReactNode;
  /** "Sign in with Ministry account", shown on the first step only when a live directory provider exists. */
  ministry?: ReactNode;
  /** The app's heading above the steps ("Case Management", "Platform Console"). */
  heading?: ReactNode;
  /**
   * Whether the server asks for an authenticator code after the password (`Auth:RequireMfa`). When false the password (or the first
   * change of a temporary password) opens the session: no code or enrolment step is shown or named. Default true.
   */
  mfa?: boolean;
  className?: string;
}

function Stepper({ items }: { items: Array<{ label: string; state: "done" | "on" | "todo" }> }) {
  return (
    <ol className="mb-5 flex items-center gap-2 text-xs font-semibold text-sand-600">
      {items.map((it, i) => (
        <li key={it.label} className={cn("flex min-w-0 items-center gap-2", i < items.length - 1 && "flex-1")}>
          <span className={cn("flex items-center gap-1.5", it.state === "on" && "text-aeblack-950", it.state === "done" && "text-success-700")} aria-current={it.state === "on" ? "step" : undefined}>
            <span className={cn("grid size-[22px] shrink-0 place-items-center rounded-full text-[12px] font-bold", it.state === "on" ? "bg-aeblack-950 text-white" : it.state === "done" ? "bg-success-600 text-white" : "bg-sand-200 text-sand-700")}>
              {it.state === "done" ? <Check className="size-3" strokeWidth={3} /> : i + 1}
            </span>
            <span className="whitespace-nowrap">{it.label}</span>
          </span>
          {i < items.length - 1 ? <span aria-hidden className="h-px min-w-3 flex-1 bg-sand-300" /> : null}
        </li>
      ))}
    </ol>
  );
}

function ErrorLine({ flow, t, id }: { flow: StaffSignInFlow; t: StaffSignInCardProps["t"]; id: string }) {
  const e = flow.error;
  if (!e) return null;
  const main = t(`err.${e.code}`, { defaultValue: t("err.generic") });
  const extra =
    e.code === "invalid_credentials" && typeof e.attemptsLeft === "number" ? t("err.attemptsLeft", { count: e.attemptsLeft }) :
    e.code === "mfa_invalid" && typeof e.attemptsLeft === "number" ? t("err.codeAttemptsLeft", { count: e.attemptsLeft }) : null;
  const policy = e.code === "password_policy" && e.errors?.length ? e.errors.map((c) => t(`err.${c}`, { defaultValue: c })) : [];
  return (
    <div id={id} data-testid="signin-error" data-code={e.code} role="alert" aria-live="polite" className="mt-4">
      <InlineAlert tone="danger" className="text-start">
        <p>{main}</p>
        {extra ? <p className="mt-1">{extra}</p> : null}
        {policy.length ? <ul className="mt-1 list-disc ps-4">{policy.map((p) => <li key={p}>{p}</li>)}</ul> : null}
      </InlineAlert>
    </div>
  );
}

function FieldLabel({ htmlFor, children, end }: { htmlFor: string; children: ReactNode; end?: ReactNode }) {
  return (
    <span className="mb-1.5 flex items-baseline justify-between gap-2">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-aeblack-950">{children}</label>
      {end ? <span className="text-xs text-sand-600">{end}</span> : null}
    </span>
  );
}

const inputCls =
  "h-11 w-full rounded-[10px] border border-sand-300 bg-white ps-10 pe-3 text-[15px] text-aeblack-950 placeholder:text-sand-500 focus-visible:border-aeblack-900 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-aeblack-900/10 aria-invalid:border-danger-500";

function PasswordField({ id, name, label, value, onChange, autoComplete, t, invalid, describedBy, autoFocus, end }: {
  id: string; name: string; label: string; value: string; onChange: (v: string) => void; autoComplete: string; t: StaffSignInCardProps["t"]; invalid?: boolean; describedBy?: string; autoFocus?: boolean; end?: ReactNode;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="mt-4">
      <FieldLabel htmlFor={id} end={end}>{label}</FieldLabel>
      <div className="relative">
        <Lock className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-sand-500" aria-hidden />
        <input id={id} name={name} type={shown ? "text" : "password"} dir="ltr" value={value} autoComplete={autoComplete} autoFocus={autoFocus} spellCheck={false} aria-invalid={invalid || undefined} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} className={cn(inputCls, "pe-10 text-start")} />
        <button type="button" data-testid="password-toggle" onClick={() => setShown((s) => !s)} aria-label={shown ? t("hidePassword") : t("showPassword")} title={shown ? t("hidePassword") : t("showPassword")} aria-pressed={shown} className="absolute end-1.5 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-md text-sand-600 hover:bg-sand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aegold-500">
          {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  );
}

export function StaffSignInCard({ flow, t, maskedLogin, rules, notice, ministry, heading, mfa = true, className }: StaffSignInCardProps) {
  const uid = useId();
  const errId = `${uid}-err`;
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [caps, onKey] = useCapsLock();
  const first = useRef<HTMLDivElement>(null);
  const invalid = !!flow.error;

  // A refused code is cleared, so the next one is typed into an empty field.
  useEffect(() => {
    if (flow.error && ["mfa_invalid", "mfa_code_reused", "code_incomplete"].includes(flow.error.code)) setCode("");
  }, [flow.error]);

  // Focus moves to the first field of each step; the code is cleared when a step opens again.
  useEffect(() => {
    setCode("");
    if (flow.step === "password") setPassword("");
    const el = first.current?.querySelector<HTMLInputElement>("input:not([type=hidden])");
    el?.focus();
  }, [flow.step]);

  const setup = flow.firstLogin || flow.step === "password_change" || flow.step === "mfa_enrol";
  // Password only: one step (no indicator), or "New password → Signed in" on a first sign-in.
  const steps: ReadonlyArray<{ label: string; state: "done" | "on" | "todo" }> | null = !mfa
    ? setup
      ? [
          { label: t("change.new"), state: flow.step === "password_change" ? "on" : "done" },
          { label: t("signedIn"), state: flow.step === "done" ? "done" : "todo" },
        ]
      : null
    : setup
      ? [
          { label: t("change.new"), state: flow.step === "password_change" ? "on" : "done" },
          { label: t("enrol.title"), state: flow.step === "mfa_enrol" ? "on" : flow.step === "done" ? "done" : "todo" },
        ]
      : [
          { label: t("password"), state: flow.step === "password" ? "on" : "done" },
          { label: t("code.title"), state: flow.step === "mfa" ? "on" : flow.step === "done" ? "done" : "todo" },
        ];

  const r = rules(newPassword, confirm);
  const busy = flow.busy || flow.step === "done";

  return (
    <section aria-labelledby={`${uid}-h`} className={cn("w-full rounded-2xl border border-sand-200 bg-white p-6 text-start shadow-sand sm:p-7", className)}>
      {heading ? <div className="mb-4">{heading}</div> : null}
      <h2 id={`${uid}-h`} className="sr-only">{t("title")}</h2>
      {steps ? <Stepper items={steps.map((s) => ({ ...s }))} /> : null}
      {notice ? <div className="mb-4">{notice}</div> : null}

      <div ref={first}>
        {flow.step === "password" ? (
          <form noValidate onSubmit={(e) => { e.preventDefault(); void flow.submitPassword(login, password); }}>
            <FieldLabel htmlFor={`${uid}-login`}>{t("login")}</FieldLabel>
            <div className="relative">
              <Mail className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-sand-500" aria-hidden />
              <input id={`${uid}-login`} name="login" type="text" dir="ltr" value={login} autoComplete="username" autoCapitalize="none" spellCheck={false} aria-invalid={invalid || undefined} aria-describedby={invalid ? errId : undefined} onChange={(e) => setLogin(e.target.value)} className={cn(inputCls, "text-start")} />
            </div>
            <div onKeyDown={onKey} onKeyUp={onKey}>
              <PasswordField id={`${uid}-pw`} name="password" label={t("password")} value={password} onChange={setPassword} autoComplete="current-password" t={t} invalid={invalid} describedBy={invalid ? errId : undefined} end={caps === null ? null : caps ? <span className="font-semibold text-warning-700">{t("capsLockOn")}</span> : t("capsLockOff")} />
            </div>
            <ErrorLine flow={flow} t={t} id={errId} />
            <Button type="submit" size="lg" className="mt-5 w-full" loading={busy} data-testid="signin-submit">
              {t("continue")} <ArrowRight className="rtl:-scale-x-100" />
            </Button>
            <p className="mt-3 flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs"><span className="font-semibold text-aegold-800">{t("forgot")}</span><span className="text-sand-600">{t("forgotHint")}</span></p>
            {ministry ? (
              <>
                <div className="my-4 flex items-center gap-3 text-xs text-sand-600"><span className="h-px flex-1 bg-sand-200" />{t("or")}<span className="h-px flex-1 bg-sand-200" /></div>
                {ministry}
              </>
            ) : null}
          </form>
        ) : null}

        {!mfa && flow.step === "done" ? (
          <p role="status" className="flex items-center gap-2 text-sm font-semibold text-success-700">
            <Check className="size-4" strokeWidth={3} aria-hidden /> {t("signedIn")}
          </p>
        ) : null}

        {flow.step === "mfa" || (mfa && flow.step === "done" && !flow.firstLogin) ? (
          <form noValidate onSubmit={(e) => { e.preventDefault(); if (flow.busy) return; if (code.length < 6) return flow.fail("code_incomplete"); void flow.submitCode(code); }}>
            <div className="flex items-center gap-3 rounded-xl border border-sand-200 bg-sand-50 px-3 py-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-aegold-100 text-aegold-800"><UserRound className="size-4" aria-hidden /></span>
              <span className="min-w-0 flex-1">
                <bdi dir="ltr" className="block truncate text-[13px] font-semibold text-aeblack-950" title={maskedLogin}>{maskedLogin}</bdi>
                <span className="flex items-center gap-1 text-xs text-success-700"><Check className="size-3" strokeWidth={3} aria-hidden /> {t("code.verified")}</span>
              </span>
              <button type="button" onClick={() => flow.restart()} className="shrink-0 text-xs font-semibold text-aegold-800 hover:underline">{t("code.notYou")}</button>
            </div>
            <div className="mt-4">
              <FieldLabel htmlFor={`${uid}-otp`} end={t("code.every")}>{t("code.label")}</FieldLabel>
              <OtpInput id={`${uid}-otp`} value={code} onChange={(v) => { setCode(v); if (flow.error) flow.clearError(); }} invalid={invalid} disabled={busy} aria-label={t("code.title")} aria-describedby={invalid ? errId : undefined} />
            </div>
            <ErrorLine flow={flow} t={t} id={errId} />
            <Button type="submit" size="lg" className="mt-5 w-full" loading={busy} data-testid="mfa-submit">
              <Lock /> {t("code.verify")}
            </Button>
            <p className="mt-3 flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs"><span className="font-semibold text-aegold-800">{t("code.lost")}</span><span className="text-sand-600">{t("code.lostHint")}</span></p>
          </form>
        ) : null}

        {flow.step === "password_change" ? (
          <form noValidate onSubmit={(e) => { e.preventDefault(); if (flow.busy) return; if (!(r.length && r.notTooLong)) return flow.fail(r.length ? "password_too_long" : "password_too_short"); if (!r.matches) return flow.fail("password_mismatch"); void flow.changePassword(newPassword, confirm); }}>
            <p className="text-sm leading-6 text-sand-700">{t("change.hint")}</p>
            <PasswordField id={`${uid}-new`} name="newPassword" label={t("change.new")} value={newPassword} onChange={setNewPassword} autoComplete="new-password" t={t} invalid={invalid} describedBy={`${uid}-rules`} />
            <PasswordField id={`${uid}-confirm`} name="confirmPassword" label={t("change.confirm")} value={confirm} onChange={setConfirm} autoComplete="new-password" t={t} invalid={invalid && flow.error?.code === "password_mismatch"} />
            <ul id={`${uid}-rules`} data-testid="password-rules" aria-label={t("rules.title")} className="mt-3 grid grid-cols-1 gap-x-3 gap-y-1 text-xs text-sand-700 sm:grid-cols-2">
              {([["length", r.length && r.notTooLong], ["noName", r.noIdentity], ["match", r.matches], ["notReused", null], ["notBreached", null]] as const).map(([k, ok]) => (
                <li key={k} className={cn("flex items-center gap-1.5", ok === true && "text-success-700", ok === false && "text-sand-600")}>
                  {ok === true ? <Check className="size-3.5" strokeWidth={3} aria-hidden /> : ok === false ? <X className="size-3.5" aria-hidden /> : <ShieldCheck className="size-3.5 text-sand-500" aria-hidden />}
                  {t(`rules.${k}`)}
                </li>
              ))}
            </ul>
            <ErrorLine flow={flow} t={t} id={errId} />
            <Button type="submit" size="lg" className="mt-5 w-full" loading={busy} data-testid="password-change-submit">
              <KeyRound /> {t("change.submit")}
            </Button>
          </form>
        ) : null}

        {flow.step === "mfa_enrol" || (mfa && flow.step === "done" && flow.firstLogin) ? (
          <form noValidate onSubmit={(e) => { e.preventDefault(); if (flow.busy || !flow.enrolment) return; if (code.length < 6) return flow.fail("code_incomplete"); void flow.confirmEnrolment(code); }}>
            <div className="flex items-start gap-4">
              {flow.enrolment ? (
                <img data-testid="totp-qr" src={flow.enrolment.qrSvgDataUri} alt={t("enrol.qrAlt")} width={132} height={132} className="size-[132px] shrink-0 rounded-lg border border-sand-200 bg-white p-2" />
              ) : (
                <span className="grid size-[132px] shrink-0 animate-pulse place-items-center rounded-lg border border-sand-200 bg-sand-100 text-center text-xs text-sand-600">{t("enrol.preparing")}</span>
              )}
              <div className="min-w-0 text-[13px] leading-5 text-sand-700">
                <p className="font-semibold text-aeblack-950">{t("enrol.scan")}</p>
                <p className="mt-1">{t("enrol.hint")}</p>
                <button type="button" onClick={() => setShowKey((v) => !v)} aria-expanded={showKey} className="mt-2 text-start font-semibold text-aegold-800 hover:underline">{showKey ? t("enrol.hideKey") : t("enrol.manual")}</button>
              </div>
            </div>
            {showKey && flow.enrolment ? (
              <div className="mt-3 rounded-lg border border-sand-200 bg-sand-50 p-3 text-xs">
                <p className="text-sand-600">{t("enrol.key")}</p>
                <div className="mt-1 flex items-center gap-2">
                  <code data-testid="totp-secret" dir="ltr" className="min-w-0 flex-1 select-all break-all font-mono text-[13px] font-semibold tracking-wider text-aeblack-950">{flow.enrolment.secret}</code>
                  <Button type="button" size="sm" variant="outline" onClick={() => { void navigator.clipboard?.writeText(flow.enrolment!.secret.replace(/\s/g, "")).then(() => setCopied(true)); }}>
                    {copied ? <Check /> : <Copy />} {copied ? t("enrol.copied") : t("enrol.copy")}
                  </Button>
                </div>
                <p className="mt-2 text-sand-600">{t("enrol.account")}: <bdi dir="ltr">{flow.enrolment.account}</bdi> · <bdi dir="ltr">{flow.enrolment.issuer}</bdi></p>
              </div>
            ) : null}
            <div className="mt-4">
              <FieldLabel htmlFor={`${uid}-otp2`}>{t("enrol.confirm")}</FieldLabel>
              <OtpInput id={`${uid}-otp2`} value={code} onChange={(v) => { setCode(v); if (flow.error) flow.clearError(); }} invalid={invalid} disabled={busy || !flow.enrolment} aria-label={t("enrol.confirm")} aria-describedby={invalid ? errId : undefined} />
            </div>
            <ErrorLine flow={flow} t={t} id={errId} />
            <Button type="submit" size="lg" className="mt-5 w-full" loading={busy} disabled={!flow.enrolment} data-testid="enrol-submit">
              <ShieldCheck /> {t("enrol.submit")}
            </Button>
            <p className="mt-3 text-xs leading-5 text-sand-600">{t("enrol.private")}</p>
          </form>
        ) : null}
      </div>
    </section>
  );
}
